import { TypedDocumentNode } from "@graphql-typed-document-node/core";
import { generateNKeysBetween } from "fractional-indexing";
import { useMemo } from "react";
import { Client, useClient } from "urql";
import { generateId } from "../../common/UrlFriendlyNanoIdGeneration";
import PlaylistData from "../data/PlaylistData";
import { PlaylistItemData } from "../data/PlaylistItemData";
import {
  addPlaylistItemMutation,
  assignPlaylistToCrewMutation,
  createPlaylistMutation,
  deletePlaylistMutation,
  movePlaylistItemMutation,
  removePlaylistItemMutation,
  renamePlaylistMutation,
} from "./playlistDocuments";
import { positionAt } from "./PlaylistItemOrder";

export const duplicatorPostfix = " (Kopie)";

/** Sends a mutation and does not wait for it: offline it waits in the queue, the cache shows it at once. */
function mutate<D, V extends { changedAt: unknown }>(
  client: Client,
  document: TypedDocumentNode<D, V>,
  variables: Omit<V, "changedAt">,
): void {
  client
    // changedAt is set by changedAtExchange
    .mutation(document, { ...variables, changedAt: undefined } as V)
    .toPromise()
    .then((result) => {
      if (result.error) {
        console.warn("Playlist mutation failed.", result.error);
      }
    });
}

interface NewPlaylist {
  title?: string;
  crewId: string | undefined;
  tags?: readonly string[];
  songEntryIds?: readonly string[];
}

/** All changes to playlists, one mutation per change (ADR-11). */
export function playlistMutations(client: Client) {
  function createPlaylist({
    title,
    crewId,
    tags,
    songEntryIds = [],
  }: NewPlaylist): string {
    const id = generateId();
    const now = new Date();
    const positions = generateNKeysBetween(null, null, songEntryIds.length);
    mutate(client, createPlaylistMutation, {
      id,
      title: title ?? "Playlist vom " + now.toLocaleString(),
      crewId: crewId ?? null,
      tags: [...(tags ?? [PlaylistData.createTimestampTag(now)])],
      items: songEntryIds.map((songId, index) => ({
        id: generateId(),
        position: positions[index],
        songId,
        title: null,
        note: null,
      })),
    });
    return id;
  }

  return {
    createPlaylist,

    /** Copies get new ids for the playlist and every item. Returns the new ids. */
    copyPlaylists(
      playlists: readonly PlaylistData[],
      crewId: string | undefined,
    ): string[] {
      return playlists.map((p) =>
        createPlaylist({
          title:
            p.belongsToCrew === crewId ? p.title + duplicatorPostfix : p.title,
          crewId,
          tags: p.getTags(),
          songEntryIds: p.getItems().map((i) => i.songEntryId),
        }),
      );
    },

    movePlaylists(
      playlists: readonly PlaylistData[],
      crewId: string | undefined,
    ) {
      playlists.forEach((p) =>
        mutate(client, assignPlaylistToCrewMutation, {
          id: p.id,
          crewId: crewId ?? null,
        }),
      );
    },

    deletePlaylists(playlists: readonly PlaylistData[]) {
      playlists.forEach((p) =>
        mutate(client, deletePlaylistMutation, { id: p.id }),
      );
    },

    renamePlaylist(id: string, title: string) {
      mutate(client, renamePlaylistMutation, { id, title });
    },

    /** Inserts after `afterItemId`, or at the end. Returns the new item. */
    insertItem(
      playlist: PlaylistData,
      songEntryId: string,
      afterItemId?: string,
    ): PlaylistItemData {
      const items = playlist.getItems();
      const afterIndex = items.findIndex((i) => i.id === afterItemId);
      const index = afterIndex === -1 ? items.length : afterIndex + 1;
      const item = {
        id: generateId(),
        position: positionAt(items, index),
        songEntryId,
      };
      mutate(client, addPlaylistItemMutation, {
        id: playlist.id,
        item: {
          id: item.id,
          position: item.position,
          songId: songEntryId,
          title: null,
          note: null,
        },
      });
      return item;
    },

    /** Moves an item to `newIndex` of the list without the item itself; `items` is the order the user sees. */
    moveItem(
      playlist: PlaylistData,
      itemId: string,
      newIndex: number,
      items: readonly PlaylistItemData[] = playlist.getItems(),
    ) {
      const others = items.filter((i) => i.id !== itemId);
      mutate(client, movePlaylistItemMutation, {
        id: playlist.id,
        itemId,
        position: positionAt(others, newIndex),
      });
    },

    removeItems(playlist: PlaylistData, itemIds: readonly string[]) {
      itemIds.forEach((itemId) =>
        mutate(client, removePlaylistItemMutation, { id: playlist.id, itemId }),
      );
    },
  };
}

export type PlaylistMutations = ReturnType<typeof playlistMutations>;

export function usePlaylistMutations(): PlaylistMutations {
  const client = useClient();
  return useMemo(() => playlistMutations(client), [client]);
}
